import { API_BASE, apiFetch, withApiRequestScope } from "../lib/api";
import { HierarchyAccessError, id, loadHierarchy, object, type HierarchyScope } from "./api";
import { browserPersistence, type SendPersistence } from "./send";

export interface LocationSelection { accountId: string; workspaceId?: string }
interface LocationInput { id: string; name: string; idempotencyKey: string; slug?: string }
export interface LocationSnapshot { scope: string[]; name: string; pending?: LocationInput; created?: LocationInput; unavailableCreatedId?: string }

export async function openLocationCreator(selection: LocationSelection, identity: HierarchyScope, persistence = browserPersistence()): Promise<LocationCreator> {
  id(selection.accountId); id(identity.userId);
  if (selection.workspaceId) id(selection.workspaceId);
  if (identity.accountId !== selection.accountId) throw new HierarchyAccessError();
  const scope = [API_BASE, identity.userId, selection.accountId, selection.workspaceId ?? ""];
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(scope)));
  const key = "chill.location-create.v1." + Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, "0")).join("");
  return new LocationCreator(selection, scope, key, persistence);
}

// One saved request per account/location. Web Locks and server idempotency keep
// response loss, reloads and a second tab from creating duplicate locations.
export class LocationCreator {
  constructor(private readonly selection: LocationSelection, private readonly scope: string[], private readonly key: string, private readonly persistence: SendPersistence) {}
  private async authorize(signal?: AbortSignal) {
    const catalog = await loadHierarchy(this.selection, signal);
    const allowed = this.selection.workspaceId ? catalog.workspace?.accessRole === "admin" : ["owner", "manager"].includes(catalog.scope.role ?? "");
    if (!allowed || catalog.scope.accountId !== this.scope[2] || catalog.scope.userId !== this.scope[1]) throw new HierarchyAccessError();
    return catalog;
  }
  private path() { return this.selection.workspaceId ? `/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}/projects` : "/code/workspaces"; }
  private saved(): LocationSnapshot {
    const raw = this.persistence.getItem(this.key);
    if (raw === null) return { scope: this.scope, name: "" };
    const value = object(JSON.parse(raw));
    if (Object.keys(value).some(k => !["scope", "name", "pending", "created"].includes(k)) || JSON.stringify(value.scope) !== JSON.stringify(this.scope) || typeof value.name !== "string" || (value.pending && value.created)) throw new HierarchyAccessError();
    const validate = (raw: unknown): LocationInput | undefined => {
      if (raw === undefined) return undefined;
      const input = object(raw);
      if (Object.keys(input).some(k => !["id", "name", "idempotencyKey", "slug"].includes(k)) || typeof input.name !== "string" || !input.name.trim() || input.idempotencyKey !== input.id || (this.selection.workspaceId ? input.slug !== undefined : input.slug !== `workspace-${input.id}`)) throw new HierarchyAccessError();
      return { id: id(input.id), name: input.name, idempotencyKey: id(input.idempotencyKey), ...(input.slug ? { slug: String(input.slug) } : {}) };
    };
    const pending = validate(value.pending), created = validate(value.created);
    if ((pending && pending.name !== value.name) || (created && created.name !== value.name)) throw new HierarchyAccessError();
    return { scope: this.scope, name: value.name, pending, created };
  }
  private persist(value: LocationSnapshot) { this.persistence.setItem(this.key, JSON.stringify(value)); }
  private validate(raw: unknown, expected: LocationInput) {
    const value = object(raw);
    if (value.id !== expected.id || value.accountId !== this.selection.accountId || (this.selection.workspaceId && value.workspaceId !== this.selection.workspaceId)) throw new HierarchyAccessError();
  }
  read(signal?: AbortSignal): Promise<LocationSnapshot> {
    return withApiRequestScope(current => this.persistence.withLock(this.key, current, async () => {
      const catalog = await this.authorize(current);
      const value = this.saved();
      if (value.created) {
        const row = (this.selection.workspaceId ? catalog.projects : catalog.workspaces).find(row => row.id === value.created!.id);
        if (!row) return { scope: this.scope, name: "", unavailableCreatedId: value.created.id };
        this.validate(row, value.created);
      }
      current.throwIfAborted(); return value;
    }), signal);
  }
  async saveName(name: string, signal?: AbortSignal): Promise<LocationSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      const value = this.saved();
      if (value.pending || value.created) throw new HierarchyAccessError();
      signal?.throwIfAborted(); value.name = name; this.persist(value); return value;
    });
  }
  create(name: string, signal?: AbortSignal): Promise<LocationSnapshot> {
    return withApiRequestScope(current => this.persistence.withLock(this.key, current, async () => {
      const catalog = await this.authorize(current);
      const value = this.saved();
      if (value.name !== name || !name.trim() || new TextEncoder().encode(name).length > 512) throw new HierarchyAccessError();
      if (value.created) {
        this.validate((this.selection.workspaceId ? catalog.projects : catalog.workspaces).find(row => row.id === value.created!.id), value.created);
        current.throwIfAborted(); return value;
      }
      if (!value.pending) {
        const uuid = crypto.randomUUID();
        value.pending = { id: uuid, name, idempotencyKey: uuid, ...(!this.selection.workspaceId ? { slug: `workspace-${uuid}` } : {}) };
      }
      current.throwIfAborted(); this.persist(value);
      const body = this.selection.workspaceId ? { ...value.pending, visibility: "workspace", state: "planned" } : { ...value.pending, accountId: this.selection.accountId };
      const response = await apiFetch(this.path(), { method: "POST", credentials: "include", cache: "no-store", signal: current, headers: { "content-type": "application/json" }, body: JSON.stringify(body) }, { retries: 0 });
      if (!response.ok) throw new Error("Creation could not be confirmed. Retry the saved request.");
      this.validate(await response.json(), value.pending);
      await this.authorize(current); current.throwIfAborted();
      value.created = value.pending; value.pending = undefined; this.persist(value); return value;
    }), signal);
  }
  startAnother(signal?: AbortSignal): Promise<LocationSnapshot> {
    return withApiRequestScope(current => this.persistence.withLock(this.key, current, async () => {
      await this.authorize(current);
      if (!this.saved().created) throw new HierarchyAccessError();
      current.throwIfAborted(); const value = { scope: this.scope, name: "" }; this.persist(value); return value;
    }), signal);
  }
}
